import { Button } from "@components/buttons/button";
import { Card } from "@components/cards/card";
import { Close } from "@components/buttons/close";
import { ModalDefaultMessage } from "@components/modals/modal-messages";
import { Pagination } from "@components/pagination";
import { LoadingSkeleton } from "@components/skeleton-loading";
import {
  IRaffleDefaultTicketPurchaseListDTO,
  IRaffleDefaultTicketPurchaseSummaryDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultTicketPurchaseDTO";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";
import { RaffleStatusTypes } from "@hooks/dtos/raffles/IRaffleGeneralDTO";
import { RaffleDefaultTicketPurchaseStatusTypes } from "@hooks/enums/raffle_default_ticket_purchase";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  getRaffleDefaultTicketPurchaseStatus,
  getRaffleStatus,
  raffleDefaultTicketPurchaseStatusSelect,
  raffleStatusTypes,
} from "@hooks/functions/Raffle/status";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FaBroom, FaCircleInfo, FaExpand, FaMinus } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";

interface IPurchaseSummaryParams {
  startDate?: string;
  endDate?: string;
  filters?: IPurchaseFilters;
}

interface IPurchaseHistoryParams {
  page?: number;
  startDate?: string;
  endDate?: string;
  filters?: IPurchaseFilters;
}

interface IPurchaseFilters {
  status?: RaffleDefaultTicketPurchaseStatusTypes;
  affiliate?: string;
  campaign?: string;
  buyer?: string;
  raffleStatus?: RaffleStatusTypes;
}

const masks = new Masks();
export function Earnings() {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Explain's Modals --- //
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalMessageKey, setModalMessageKey] = useState<string | null>(null);

  // Pagination Part
  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [order, setOrder] = useState("DESC");
  const [totalPages, setTotalPages] = useState<number>(1);

  // Filters
  const [status, setStatus] = useState<
    RaffleDefaultTicketPurchaseStatusTypes | undefined
  >();
  const [affiliate, setAffiliate] = useState<string | undefined>();
  const [campaign, setCampaign] = useState<string | undefined>();
  const [buyer, setBuyer] = useState<string | undefined>();
  const [raffleStatus, setRaffleStatus] = useState<
    RaffleStatusTypes | undefined
  >();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState<
    IRaffleDefaultDTO | undefined
  >();

  const [show, setShow] = useState<{ filter: boolean }>({
    filter: false,
  });

  const [resultTime, setResultTime] = useState("Todo Período");

  // API Returns
  const [financialData, setFinancialData] = useState<
    IRaffleDefaultTicketPurchaseSummaryDTO | undefined
  >();
  const [earnings, setEarnings] = useState<
    IRaffleDefaultTicketPurchaseListDTO[]
  >([]);

  const [isLoading, setIsLoading] = useState({
    card: false,
    history: true,
  });

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  async function getFinancialEarnings({
    page,
    startDate,
    endDate,
    filters,
  }: IPurchaseHistoryParams) {
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          order,
          ...(filters?.status && { status: filters.status }),
          ...(filters?.affiliate && {
            affiliate: filters.affiliate.replace(/^@+/, "").trim(),
          }),
          ...(filters?.campaign && { campaign: filters.campaign.trim() }),
          ...(filters?.buyer && { buyer: filters.buyer.trim() }),
          ...(filters?.raffleStatus && { raffleStatus: filters.raffleStatus }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        }),
      ).toString()}`;
      const result = await api.get(
        `/customer/financial/history/earningList${params}`,
      );
      setEarnings(result.data.list);
      setTotalPages(result.data.totalPages);
      setIsLoading((prev) => ({ ...prev, history: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, history: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function getFinancialEarningSummary({
    startDate,
    endDate,
    filters,
  }: IPurchaseSummaryParams) {
    setIsLoading((prev) => ({ ...prev, card: true }));
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          ...(filters?.status && { status: filters.status }),
          ...(filters?.affiliate && {
            affiliate: filters.affiliate.replace(/^@+/, "").trim(),
          }),
          ...(filters?.campaign && { campaign: filters.campaign.trim() }),
          ...(filters?.buyer && { buyer: filters.buyer.trim() }),
          ...(filters?.raffleStatus && { raffleStatus: filters.raffleStatus }),
          ...(startDate && { startDate }),
          ...(endDate && { endDate }),
        }),
      ).toString()}`;
      const result = await api.get(
        `/customer/financial/history/earningSummary${params}`,
      );

      setFinancialData(result.data);
      setIsLoading((prev) => ({ ...prev, card: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, card: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function cleanFilters() {
    // --- Main Filters --- //
    setStatus(undefined);
    setAffiliate(undefined);
    setCampaign(undefined);
    setBuyer(undefined);
    setRaffleStatus(undefined);
    // --- Anothers --- //
    setStartDate("");
    setEndDate("");
    handleSubmit("", "", {
      status: undefined,
      affiliate: undefined,
      campaign: undefined,
      buyer: undefined,
      raffleStatus: undefined,
    });
  }

  function getFilters(): IPurchaseFilters {
    return {
      status,
      affiliate: affiliate?.replace(/^@+/, "").trim() || undefined,
      campaign: campaign?.trim() || undefined,
      buyer: buyer?.trim() || undefined,
      raffleStatus,
    };
  }

  function handleSubmit(
    _startDate: string,
    _endDate: string,
    filters = getFilters(),
  ) {
    setIsLoading({ card: true, history: true });
    setResultTime(
      _startDate.length + _endDate.length > 0
        ? `Período Selecionado`
        : `Todo Período`,
    );
    setPage(1);
    getFinancialEarningSummary({
      startDate: _startDate,
      endDate: _endDate,
      filters,
    });
    getFinancialEarnings({
      page: 1,
      startDate: _startDate,
      endDate: _endDate,
      filters,
    });
  }

  const handleModalOpen = (messageKey: string) => {
    setModalMessageKey(messageKey);
    setIsModalOpen(true);
  };

  useEffect(() => {
    getFinancialEarningSummary({
      startDate,
      endDate,
      filters: getFilters(),
    });
    getFinancialEarnings({
      page,
      startDate,
      endDate,
      filters: getFilters(),
    });
  }, [page]);

  useEffect(() => {
    getFinancialEarningSummary({
      startDate,
      endDate,
      filters: getFilters(),
    });
    getFinancialEarnings({
      page,
      startDate,
      endDate,
      filters: getFilters(),
    });
  }, []);

  return (
    <>
      {/* Filters */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div
              className="card-header"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShow((prev) => ({
                  ...prev,
                  filter: !show.filter,
                }));
              }}
            >
              <div className="d-flex justify-content-between">
                <div className="">
                  <div className="card-title">Filtrar Registros</div>
                  <div className="card-category">
                    Utilize o filtro para trazer um período específico.
                  </div>
                </div>
                <div className="">
                  {show.filter ? (
                    <FaMinus
                      className="fs-5"
                      title="Clique para ocultar as informações."
                    />
                  ) : (
                    <FaExpand
                      className="fs-5"
                      title="Clique para visualizar as informações."
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={`card-body ${show.filter ? `` : `d-none`}`}>
              <div className="row align-items-end g-2">
                <div className="col-md-6 col-xl-4">
                  <div className="form-group mb-2">
                    <label
                      className="form-label small fw-semibold mb-1"
                      htmlFor="affiliate"
                    >
                      Afiliado
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="affiliate"
                      value={affiliate || ""}
                      placeholder="Nome ou atsign"
                      onChange={(e) => setAffiliate(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSubmit(startDate, endDate);
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="form-group mb-2">
                    <label
                      className="form-label small fw-semibold mb-1"
                      htmlFor="campaign"
                    >
                      Campanha
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="campaign"
                      value={campaign || ""}
                      placeholder="Nome ou slug"
                      onChange={(e) => setCampaign(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSubmit(startDate, endDate);
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-xl-4">
                  <div className="form-group mb-2">
                    <label
                      className="form-label small fw-semibold mb-1"
                      htmlFor="buyer"
                    >
                      Comprador
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="buyer"
                      value={buyer || ""}
                      placeholder="Nome do comprador"
                      onChange={(e) => setBuyer(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSubmit(startDate, endDate);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="row align-items-end g-2">
                <div className="col-md-6 col-xl-3">
                  <div className="form-group mb-2">
                    <label
                      className="form-label small fw-semibold mb-1"
                      htmlFor="Status"
                    >
                      Status da Aquisição
                    </label>
                    <select
                      className="form-select"
                      aria-label="Status"
                      value={status || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setStatus(
                          value === ""
                            ? undefined
                            : (value as RaffleDefaultTicketPurchaseStatusTypes),
                        );
                      }}
                    >
                      <option value="">Todos</option>
                      {raffleDefaultTicketPurchaseStatusSelect.map((x) => {
                        return (
                          <option key={x.status} value={x.status}>
                            {x.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="col-md-6 col-xl-3">
                  <div className="form-group mb-2">
                    <label
                      className="form-label small fw-semibold mb-1"
                      htmlFor="raffleStatus"
                    >
                      Status da Campanha
                    </label>
                    <select
                      className="form-select"
                      aria-label="Status da Campanha"
                      value={raffleStatus ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setRaffleStatus(
                          value === ""
                            ? undefined
                            : (value as RaffleStatusTypes),
                        );
                      }}
                    >
                      <option value="">Todos</option>
                      {raffleStatusTypes.map((x) => {
                        return (
                          <option key={x.status} value={x.status}>
                            {x.text}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div className="col-md-6 col-xl-2">
                  <div className="form-group mb-2">
                    <label
                      className="form-label small fw-semibold mb-1"
                      htmlFor="startDate"
                    >
                      Data Inicial
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setStartDate(e.target.value)}
                      id="startDate"
                      value={startDate}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-xl-2">
                  <div className="form-group mb-2">
                    <label
                      className="form-label small fw-semibold mb-1"
                      htmlFor="endDate"
                    >
                      Data Final
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      onChange={(e) => setEndDate(e.target.value)}
                      id="endDate"
                      value={endDate}
                    />
                  </div>
                </div>

                <div className="col-md-12 col-xl-2">
                  <div className="d-flex gap-2 mb-2">
                    <div className="flex-grow-1">
                      <button
                        type="button"
                        className="btn btn-success w-100"
                        onClick={() => handleSubmit(startDate, endDate)}
                      >
                        Filtrar
                      </button>
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={cleanFilters}
                        title="Limpar todos os filtros."
                      >
                        <FaBroom />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="card-title">Resumo dos Proventos</div>
              <div className="card-category">
                Rendimentos, taxas, impostos, comissões, lucro, etc...
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                {/* Total Income */}
                <div className="col-md-4">
                  <div className="card-header text-center">
                    <div className="fs-5">
                      <div className="">Total de Entradas</div>
                    </div>
                    <div className="text-muted fs-6 p-0 m-0">
                      <small>Total de entradas de clientes/vendas</small>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading.card ? (
                      <div className="w-100">
                        <LoadingSkeleton count={2} />
                      </div>
                    ) : (
                      <div className="row d-flex justify-content-between">
                        <div className="col-md-12 col-xxl-6 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              financialData?.inflowGrossTotal ?? 0,
                            )}
                          </span>
                          <br />
                          <span>Bruto</span>
                        </div>
                        <div className="col-md-12 col-xxl-6 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(financialData?.inflowTotal) ?? 0}
                          </span>
                          <br />
                          <span>Líquido</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Fees Outcomes */}
                <div className="col-md-4">
                  <div className="card-header text-center">
                    <div className="fs-5">
                      <div className="">Total de Taxas</div>
                    </div>
                    <div className="text-muted fs-6 p-0 m-0">
                      <small>Taxas Internas, Externas e Outras</small>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading.card ? (
                      <div className="w-100">
                        <LoadingSkeleton count={2} />
                      </div>
                    ) : (
                      <div className="row d-flex justify-content-between">
                        <div className="col-md-3 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              Number(financialData?.internalFeeTotal ?? 0),
                            )}
                          </span>
                          <br />
                          <span>
                            Internas{" "}
                            <FaCircleInfo title="Taxas da Plataforma." />
                          </span>
                        </div>
                        <div className="col-md-3 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              Number(financialData?.externalFeeTotal ?? 0),
                            )}
                          </span>
                          <br />
                          <span>
                            Externas{" "}
                            <FaCircleInfo title="Taxas de provedores, bancos, Gateway de Pagamentos, etc..." />
                          </span>
                        </div>
                        <div className="col-md-3 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              Number(financialData?.anotherFeeTotal ?? 0),
                            )}
                          </span>
                          <br />
                          <span>
                            Outras{" "}
                            <FaCircleInfo title="Impostos, encargos, etc..." />
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Comissions */}
                <div className="col-md-4">
                  <div className="card-header text-center">
                    <div className="fs-5">
                      <div className="">Comissões</div>
                    </div>
                    <div className="text-muted fs-6 p-0 m-0">
                      <small>Repasses para Afiliados</small>
                    </div>
                  </div>
                  <div className="card-body">
                    {isLoading.card ? (
                      <div className="w-100">
                        <LoadingSkeleton count={2} />
                      </div>
                    ) : (
                      <div className="row d-flex justify-content-between">
                        <div className="col-md-12 col-xxl-6 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.formatMilhar(
                              Number(financialData?.commissionAmount),
                            )}
                          </span>
                          <br />
                          <span>Quantidade</span>
                        </div>
                        <div className="col-md-12 col-xxl-6 text-center">
                          <span className="fs-4 fw-bold">
                            {masks.brlCurrency(
                              Number(financialData?.commissionTotal),
                            )}
                          </span>
                          <br />
                          <span>Total Pago</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
              {resultTime}
            </span>
          </Card>
        </div>
      </div>

      {/* Financial Earning Table */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                {/* Header of the Table */}
                <div className="col-md-8">
                  <h4 className="card-title">Relação de Proventos</h4>
                  <p className="card-category">Lista de vendas de tickets</p>
                </div>
                {/* Ordenate Table */}
                <div className="col-md-4">
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Quantidade de Registros por Página"
                          value={take}
                          onChange={(e) => setTake(Number(e.target.value))}
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="75">75</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Ordenação da Tabela"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                        >
                          <option value="ASC">Crescente</option>
                          <option value="DESC">Decrescente</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <button
                          className="btn btn-sm btn-success form-control px-2"
                          onClick={() => handleSubmit(startDate, endDate)}
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr role="row">
                          <th scope="col" className="fs-6">
                            #
                          </th>
                          <th>Campanha</th>
                          <th>Ticket</th>
                          <th style={{ minWidth: "10rem" }}>Cliente</th>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleModalOpen("Inflow")}
                          >
                            Bruto
                          </th>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleModalOpen("Profit")}
                          >
                            Líquido
                          </th>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleModalOpen("Commission")}
                          >
                            Comissão
                          </th>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleModalOpen("feeInternal")}
                          >
                            Taxa Int.
                          </th>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleModalOpen("feeExternal")}
                          >
                            Taxa Ext.
                          </th>
                          <th
                            style={{ cursor: "pointer" }}
                            onClick={() => handleModalOpen("feeAnother")}
                          >
                            Outras Txs
                          </th>
                          <th>Status</th>
                          <th>Data</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading.history && (
                          <tr>
                            <td colSpan={12} className="text-center">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        )}

                        {earnings &&
                          earnings.length > 0 &&
                          earnings.map((earning) => {
                            const status = getRaffleDefaultTicketPurchaseStatus(
                              earning.status,
                            );
                            const raffleStatus = earning?.raffleData?.status
                              ? getRaffleStatus(earning.raffleData.status)
                              : undefined;
                            const clientName = earning?.clientData?.name
                              ? formatName(
                                  abbreviateName(earning?.clientData?.name, 12),
                                )
                              : "~";
                            return (
                              <tr key={earning?.id}>
                                <td>{earning?.id}</td>
                                <td>
                                  <div
                                    className="d-flex flex-column gap-1"
                                    style={{
                                      width: "100%",
                                      alignItems: "flex-start",
                                      textAlign: "left",
                                    }}
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-link p-0 text-decoration-none text-start fw-semibold lh-sm"
                                      onClick={() => {
                                        navigate(
                                          `/raffle/default/view?campaign=${earning.raffleData?.campaign}&key=${earning.raffleData?.key}`,
                                        );
                                      }}
                                    >
                                      {earning.raffleData?.name ||
                                        earning.raffleData?.campaign ||
                                        "~"}
                                    </button>

                                    <div
                                      className="d-flex flex-column gap-1"
                                      style={{
                                        width: "100%",
                                        alignItems: "flex-start",
                                      }}
                                    >
                                      <span className="badge bg-info">
                                        #{earning.raffleData?.campaign || "~"}
                                      </span>

                                      {raffleStatus && (
                                        <span
                                          className={`badge bg-${raffleStatus.color}`}
                                          style={{
                                            marginLeft: 0,
                                            marginRight: "auto",
                                          }}
                                          title={raffleStatus.title}
                                        >
                                          {raffleStatus.text}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td>{earning?.ticketData?.ticket || "~"}</td>
                                <td className="">
                                  <div className="d-flex flex-column text-center">
                                    <span
                                      title={formatName(
                                        earning?.clientData?.name || "Sem Nome",
                                      )}
                                    >
                                      {clientName}
                                    </span>
                                    <small className="bg-secondary rounded text-white">
                                      @{earning?.clientData?.atSign || "~"}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  {masks?.brlCurrency(
                                    earning?.amountGross || 0,
                                  )}
                                </td>
                                <td>
                                  {masks?.brlCurrency(earning?.amount || 0)}
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="text-center">
                                      {masks?.brlCurrency(
                                        earning?.commission || 0,
                                      )}
                                    </span>
                                    {earning?.commissionTo && (
                                      <div
                                        className="text-center"
                                        title={formatName(
                                          earning?.commissionToData?.name ||
                                            "~",
                                        )}
                                      >
                                        <Button
                                          className="btn btn-info rounded text-white py-1 px-2"
                                          to={`/profile?atsign=${
                                            earning?.commissionToData?.atSign
                                          }`}
                                        >
                                          @
                                          {earning?.commissionToData?.atSign ||
                                            "~"}
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  {masks?.brlCurrency(
                                    earning?.internalFee || 0,
                                  )}
                                </td>
                                <td>
                                  {masks?.brlCurrency(
                                    earning?.externalFee || 0,
                                  )}
                                </td>
                                <td>
                                  {masks?.brlCurrency(earning?.anotherFee || 0)}
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${status.color}`}
                                    title={status.text}
                                  >
                                    {status.title}
                                  </span>
                                </td>
                                <td>
                                  {dayjs(earning?.createdAt).format(
                                    "DD/MM/YYYY [às] HH:mm:ss",
                                  )}
                                </td>
                                <td>
                                  {earning.raffleData && (
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-info py-0 px-2"
                                      title="Ver detalhes da campanha"
                                      onClick={() =>
                                        setSelectedCampaign(earning.raffleData)
                                      }
                                    >
                                      <FaCircleInfo
                                        className="me-1"
                                        title="Ver detalhes da campanha"
                                        onClick={() =>
                                          setSelectedCampaign(
                                            earning.raffleData,
                                          )
                                        }
                                      />
                                      Info
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}

                        {isLoading.history ||
                          !earnings ||
                          (earnings?.length <= 0 && (
                            <tr>
                              <td colSpan={12} className="text-center">
                                Nenhum registro encontrado.
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={3}
              />
            </div>
          </Card>
        </div>
      </div>

      {isModalOpen && modalMessageKey && (
        <ModalDefaultMessage
          messageKey={modalMessageKey}
          onClose={handleModalClose}
        />
      )}

      {selectedCampaign && (
        <CampaignInfoModal
          isOpen={!!selectedCampaign}
          raffle={selectedCampaign}
          onClose={() => setSelectedCampaign(undefined)}
        />
      )}
    </>
  );
}

interface ICampaignInfoModalProps {
  isOpen: boolean;
  raffle: IRaffleDefaultDTO;
  onClose: () => void;
}

function CampaignInfoModal({
  isOpen,
  raffle,
  onClose,
}: ICampaignInfoModalProps) {
  const [animate, setAnimate] = useState(false);
  const status = getRaffleStatus(raffle.status);

  function handleClose() {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }

  function handleEscape(evt: KeyboardEvent) {
    if (evt.key === "Escape" || evt.key === "Esc" || evt.keyCode === 27) {
      handleClose();
    }
  }

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    }

    setAnimate(false);
  }, [isOpen]);

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="campaignInfoModalLabel"
        aria-hidden={!isOpen}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold" id="campaignInfoModalLabel">
                Informações da Campanha
              </h5>
              <Close onPress={handleClose} />
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-8">
                  <h5 className="mb-1">{raffle.name || "~"}</h5>
                  <div className="text-muted">
                    {raffle.description_short || "~"}
                  </div>
                </div>
                <div className="col-md-4 text-md-end mt-3 mt-md-0">
                  <span
                    className={`badge bg-${status.color}`}
                    title={status.title}
                  >
                    {status.text}
                  </span>
                </div>
              </div>

              <div className="table-responsive mt-3">
                <table className="table table-striped mb-0">
                  <tbody>
                    <tr>
                      <th>Slug</th>
                      <td>{raffle.campaign || "~"}</td>
                    </tr>
                    <tr>
                      <th>Chave</th>
                      <td>{raffle.key || "~"}</td>
                    </tr>
                    <tr>
                      <th>Tipo</th>
                      <td>
                        {raffle.typeData?.name || raffle.typeData?.key || "~"}
                      </td>
                    </tr>
                    <tr>
                      <th>Quantidade</th>
                      <td>
                        {masks.formatMilhar(Number(raffle.quantity || 0))}
                      </td>
                    </tr>
                    <tr>
                      <th>Preço do Ticket</th>
                      <td>{masks.brlCurrency(Number(raffle.price || 0))}</td>
                    </tr>
                    <tr>
                      <th>Prêmio</th>
                      <td>
                        {masks.brlCurrency(
                          Number(raffle.rewardPriceMarket || 0),
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th>Criada em</th>
                      <td>
                        {raffle.createdAt
                          ? dayjs(raffle.createdAt).format(
                              "DD/MM/YYYY [às] HH:mm:ss",
                            )
                          : "~"}
                      </td>
                    </tr>
                    <tr>
                      <th>Finalizada em</th>
                      <td>
                        {raffle.finishedAt
                          ? dayjs(raffle.finishedAt).format(
                              "DD/MM/YYYY [às] HH:mm:ss",
                            )
                          : "~"}
                      </td>
                    </tr>
                    <tr>
                      <th>Proprietário</th>
                      <td>
                        {formatName(raffle.customerData?.name || "~")}
                        {raffle.customerData?.atSign && (
                          <Link
                            className="bg-secondary px-2 py-1 rounded text-white ms-2"
                            to={`/profile?atSign=${raffle.customerData?.atSign}`}
                          >
                            @{raffle.customerData.atSign}
                          </Link>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <Link
                className="btn btn-secondary text-white ms-2"
                to={`/raffle/default/view?campaign=${raffle.campaign}&key=${raffle.key}`}
              >
                Ver Campanha
              </Link>
              <button
                type="button"
                className="btn btn-primary px-4"
                onClick={handleClose}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      ></div>
    </>
  );
}
